import pako from 'pako';

export interface AudioPayload {
    audio_data: string;
    sample_rate: number;
    n_samples: number;
    dtype: string;
    compression: string;
    encoding: string;
    file: string;
}

/**
 * Multi-channel audio data ready for Web Audio API consumption.
 * channels[0] = left (or mono), channels[1] = right (stereo only).
 * length = samples per channel (N), not total samples.
 */
export interface DecompressedAudio {
    channels: Float32Array[];
    numberOfChannels: number;
    length: number;
}

/**
 * Decompress audio data retrieved from Qdrant.
 *
 * The factory uploads stereo files with librosa.load(mono=False), producing shape (2, N).
 * The blob is serialized row-major: [ch0 * N int16][ch1 * N int16].
 * n_samples is stored as len(audio_data) which equals 2 for stereo (channel count, not sample count).
 *
 * Detection: n_samples <= 8 means the factory stored the channel count, not the sample count.
 * For stereo: split the blob into ch0 (first N) and ch1 (last N).
 * For mono: use all samples as a single channel.
 */
export function decompressAudio(payload: AudioPayload): DecompressedAudio {
    const binaryStr = atob(payload.audio_data);
    const bytes = Uint8Array.from(binaryStr, c => c.charCodeAt(0));
    const decompressed = pako.inflate(bytes);
    const int16Array = new Int16Array(decompressed.buffer);

    const isStereo = payload.n_samples <= 8;

    if (isStereo) {
        const samplesPerChannel = Math.floor(int16Array.length / 2);
        const left = new Float32Array(samplesPerChannel);
        const right = new Float32Array(samplesPerChannel);
        for (let i = 0; i < samplesPerChannel; i++) {
            left[i] = int16Array[i] / 32767;
            right[i] = int16Array[samplesPerChannel + i] / 32767;
        }
        return { channels: [left, right], numberOfChannels: 2, length: samplesPerChannel };
    }

    const samplesPerChannel = int16Array.length;
    const mono = new Float32Array(samplesPerChannel);
    for (let i = 0; i < samplesPerChannel; i++) {
        mono[i] = int16Array[i] / 32767;
    }
    return { channels: [mono], numberOfChannels: 1, length: samplesPerChannel };
}

/**
 * Concatenate multiple DecompressedAudio objects channel-by-channel.
 * All inputs must have the same numberOfChannels.
 */
export function concatenateAudio(audioSegments: DecompressedAudio[]): DecompressedAudio {
    if (audioSegments.length === 0) {
        throw new Error('No audio segments to concatenate');
    }

    const numberOfChannels = audioSegments[0].numberOfChannels;
    const totalLength = audioSegments.reduce((sum, a) => sum + a.length, 0);

    const channels: Float32Array[] = Array.from(
        { length: numberOfChannels },
        () => new Float32Array(totalLength)
    );

    let offset = 0;
    for (const segment of audioSegments) {
        for (let c = 0; c < numberOfChannels; c++) {
            channels[c].set(segment.channels[c], offset);
        }
        offset += segment.length;
    }

    return { channels, numberOfChannels, length: totalLength };
}
